import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ManualPunchCard } from './ManualPunchCard';
import {
  CheckCircle2, Hourglass, ShieldCheck, Edit3, Clock, RefreshCw, X, Search, Filter, AlertTriangle
} from 'lucide-react';

const todayISO = () => new Date().toISOString().split('T')[0];
const daysAgoISO = (n) => new Date(Date.now() - n * 86400_000).toISOString().split('T')[0];
const fmtTime = (t) => t || '-';
const fmtNum = (n) => (n == null ? '-' : Number(n).toFixed(2));
const fmtDateLocal = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

// A record is "incomplete" (needs correction) when:
//  - It's a PAST date (before today), AND at least one of:
//    a. The staff clocked SOMETHING but is missing either the in or the out.
//    b. The record has NO resolved branch (device wasn't mapped or the
//       shift was on a floating device) - this blocks tip apportionment.
// We don't flag today's records because the shift may still be in progress,
// and we don't flag totally-empty records because that's a no-show/leave day.
const isIncompleteRecord = (r) => {
  if (!r || !r.date) return false;
  if (r.date >= todayISO()) return false;
  const hasIn = !!r.in_time;
  const hasOut = !!r.out_time;
  const hasBreak = Array.isArray(r.breaks) && r.breaks.some(b => b && (b.in || b.out));
  const anyStamp = hasIn || hasOut || hasBreak;
  if (!anyStamp) return false;
  // Leave days are legitimately open (no punches)
  if (r.leave_type) return false;
  const missingPunch = (!hasIn && (hasOut || hasBreak)) || (!hasOut && (hasIn || hasBreak));
  const missingBranch = !((r.branch || '').toString().trim());
  return missingPunch || missingBranch;
};

// Human-readable reason - used for the badge tooltip / row hint.
const incompleteReasonFor = (r) => {
  if (!r) return null;
  if (!r.in_time) return 'Missing clock in';
  if (!r.out_time) return 'Missing clock out';
  if (!((r.branch || '').toString().trim())) return 'Missing branch';
  return null;
};

// Compute break duration in minutes from a "HH:mm" string pair
const breakMinutes = (out, back) => {
  if (!out || !back) return null;
  const parse = (s) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(s);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
  };
  const a = parse(out);
  const b = parse(back);
  if (a == null || b == null) return null;
  const d = b - a;
  return d > 0 ? d : null;
};

const fmtBreakMins = (n) => (n == null ? '' : `${n} min`);

/**
 * AttendanceView
 *  mode: "staff" -> shows own records (employee sign-off + edit request)
 *  mode: "manager" -> shows team records (manager sign-off + edit request on behalf of any team member)
 */
export function AttendanceView({ mode = 'staff', teamUsers = [] }) {
  const [dateFrom, setDateFrom] = useState(daysAgoISO(14));
  const [dateTo, setDateTo] = useState(todayISO());
  const [userFilter, setUserFilter] = useState('all');
  const [signoffFilter, setSignoffFilter] = useState('any');
  const [sourceFilter, setSourceFilter] = useState('any');
  const [dayTypeFilter, setDayTypeFilter] = useState('any');
  const [search, setSearch] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openEdit, setOpenEdit] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [editChanges, setEditChanges] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingMineOnly, setPendingMineOnly] = useState(true);
  const [incompleteOnly, setIncompleteOnly] = useState(false);

  // Payroll period summary (26th of prev month -> 25th of selected month)
  const currentPayrollPeriodKey = () => {
    const now = new Date();
    const d = now.getDate();
    let y = now.getFullYear();
    let m = now.getMonth() + 1;
    if (d >= 26) {
      m += 1;
      if (m === 13) { m = 1; y += 1; }
    }
    return `${y}-${String(m).padStart(2, '0')}`;
  };
  const [payrollPeriod, setPayrollPeriod] = useState(currentPayrollPeriodKey());
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  // Build the last ~15 payroll period options (current + 14 previous)
  const payrollPeriodOptions = useMemo(() => {
    const opts = [];
    const [cy, cm] = currentPayrollPeriodKey().split('-').map(Number);
    let y = cy, m = cm;
    for (let i = 0; i < 15; i++) {
      const key = `${y}-${String(m).padStart(2, '0')}`;
      const startM = m === 1 ? 12 : m - 1;
      const startY = m === 1 ? y - 1 : y;
      const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      opts.push({
        key,
        label: `${monthNames[startM - 1]} 26 - ${monthNames[m - 1]} 25, ${y}`,
      });
      m -= 1;
      if (m === 0) { m = 12; y -= 1; }
    }
    return opts;
  }, []);
  const fetchPayrollSummary = useCallback(async () => {
    if (mode !== 'staff') return;
    setPayrollLoading(true);
    try {
      const r = await axios.get(`${API}/attendance/payroll-period-summary`, {
        params: { period: payrollPeriod },
      });
      setPayrollSummary(r.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load payroll summary');
    } finally {
      setPayrollLoading(false);
    }
  }, [mode, payrollPeriod]);
  useEffect(() => { fetchPayrollSummary(); }, [fetchPayrollSummary]);

  const userMap = useMemo(() => {
    const m = {};
    teamUsers.forEach(u => { m[u.id] = u; });
    return m;
  }, [teamUsers]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (mode === 'manager' && userFilter !== 'all') params.user_id = userFilter;
      if (mode === 'manager' && signoffFilter !== 'any') params.signoff_status = signoffFilter;
      if (mode === 'manager' && sourceFilter !== 'any') params.source = sourceFilter;
      const url = mode === 'staff' ? `${API}/attendance/me` : `${API}/attendance`;
      const r = await axios.get(url, { params });
      setRecords(r.data.records || []);
    } catch (e) {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, mode, userFilter, signoffFilter, sourceFilter]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Records still awaiting the CURRENT user's sign-off
  //  - staff mode: employee_signoff is missing
  //  - manager mode: manager_signoff is missing (regardless of employee_signoff)
  const pendingMineCount = useMemo(() => {
    return records.reduce((acc, r) => {
      const needed = mode === 'staff' ? !r.employee_signoff : !r.manager_signoff;
      return acc + (needed ? 1 : 0);
    }, 0);
  }, [records, mode]);

  // Past records that have SOME punches but are missing either the clock in
  // or the clock out - these need correction before they can be signed off.
  const incompleteCount = useMemo(() => {
    return records.reduce((acc, r) => acc + (isIncompleteRecord(r) ? 1 : 0), 0);
  }, [records]);

  // Client-side filters that don't need a backend refetch
  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      if (pendingMineOnly) {
        const needed = mode === 'staff' ? !r.employee_signoff : !r.manager_signoff;
        if (!needed) return false;
      }
      if (incompleteOnly && !isIncompleteRecord(r)) return false;
      if (dayTypeFilter === 'weekday' && (r.is_sunday || r.is_public_holiday)) return false;
      if (dayTypeFilter === 'sunday' && !r.is_sunday) return false;
      if (dayTypeFilter === 'ph' && !r.is_public_holiday) return false;
      if (dayTypeFilter === 'leave' && !r.leave_type) return false;
      if (search) {
        const q = search.toLowerCase();
        const u = r.user_id ? userMap[r.user_id] : null;
        const hay = `${r.name || ''} ${r.employee_id || ''} ${u?.username || ''} ${u?.first_name || ''} ${u?.last_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [records, dayTypeFilter, search, userMap, pendingMineOnly, incompleteOnly, mode]);

  const clearFilters = () => {
    setUserFilter('all');
    setSignoffFilter('any');
    setSourceFilter('any');
    setDayTypeFilter('any');
    setSearch('');
    setPendingMineOnly(false);
    setIncompleteOnly(false);
    setDateFrom(daysAgoISO(14));
    setDateTo(todayISO());
  };

  const signoff = async (record, action) => {
    try {
      const verb = mode === 'staff' ? 'employee' : 'manager';
      if (action === 'sign') {
        await axios.post(`${API}/attendance/${record.id}/signoff/${verb}`);
        toast.success('Signed off');
      } else {
        await axios.delete(`${API}/attendance/${record.id}/signoff/${verb}`);
        toast.success('Sign-off revoked');
      }
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const submitEdit = async () => {
    if (!editReason.trim() || !editChanges.trim()) {
      toast.error('Reason and requested changes are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/attendance/${openEdit.id}/edit-request`, {
        reason: editReason,
        requested_changes: editChanges,
      });
      toast.success('Edit request submitted');
      setOpenEdit(null);
      setEditReason('');
      setEditChanges('');
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`attendance-view-${mode}`}>
      {mode === 'staff' && <ManualPunchCard onPunched={() => { fetchRecords(); fetchPayrollSummary(); }} />}
      {/* Payroll Period Summary - staff only. 26th → 25th window. Shows
          totals from fully signed-off days plus the indicative pay. */}
      {mode === 'staff' && (
        <div className="bg-gradient-to-br from-emerald-50 to-stone-50 border border-emerald-200 rounded-xl p-4" data-testid="payroll-summary-panel">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <div className="text-sm font-semibold text-stone-800 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Payroll period summary
              </div>
              <div className="text-xs text-stone-500 mt-0.5">
                {payrollSummary?.period?.label || 'Loading…'} · signed-off hours only
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={payrollPeriod} onValueChange={setPayrollPeriod}>
                <SelectTrigger className="w-[220px] bg-white" data-testid="payroll-period-select">
                  <SelectValue placeholder="Pick payroll period" />
                </SelectTrigger>
                <SelectContent>
                  {payrollPeriodOptions.map(o => (
                    <SelectItem key={o.key} value={o.key} data-testid={`payroll-period-${o.key}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={fetchPayrollSummary} disabled={payrollLoading}
                data-testid="payroll-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${payrollLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          {payrollSummary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div className="bg-white rounded border border-stone-200 p-2">
                  <div className="text-xs text-stone-500">Normal hours</div>
                  <div className="font-mono text-lg text-stone-800" data-testid="pay-hrs-normal">{payrollSummary.hours.ordinary.toFixed(2)}</div>
                  <div className="text-xs text-stone-500">R{payrollSummary.pay.normal.toFixed(2)}</div>
                </div>
                <div className="bg-white rounded border border-stone-200 p-2">
                  <div className="text-xs text-stone-500">Overtime</div>
                  <div className="font-mono text-lg text-stone-800" data-testid="pay-hrs-overtime">{payrollSummary.hours.overtime.toFixed(2)}</div>
                  <div className="text-xs text-stone-500">R{payrollSummary.pay.overtime.toFixed(2)} <span className="text-stone-400">(x1.5)</span></div>
                </div>
                <div className="bg-white rounded border border-stone-200 p-2">
                  <div className="text-xs text-stone-500">Sunday hours</div>
                  <div className="font-mono text-lg text-stone-800" data-testid="pay-hrs-sunday">{payrollSummary.hours.sunday.toFixed(2)}</div>
                  <div className="text-xs text-stone-500">R{payrollSummary.pay.sunday.toFixed(2)} <span className="text-stone-400">(x1.5)</span></div>
                </div>
                <div className="bg-white rounded border border-stone-200 p-2">
                  <div className="text-xs text-stone-500">Public holiday</div>
                  <div className="font-mono text-lg text-stone-800" data-testid="pay-hrs-ph">{payrollSummary.hours.public_holiday_total.toFixed(2)}</div>
                  <div className="text-xs text-stone-500">R{payrollSummary.pay.public_holiday.toFixed(2)} <span className="text-stone-400">(x2)</span></div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
                <div className="text-xs text-stone-500">
                  {payrollSummary.days_worked} day(s) worked ·{' '}
                  {payrollSummary.signed_off_count} signed-off ·{' '}
                  {payrollSummary.unsigned_count > 0 ? (
                    <span className="text-amber-700 font-medium">{payrollSummary.unsigned_count} still awaiting sign-off (not in totals)</span>
                  ) : (
                    <span className="text-emerald-700">all days signed off</span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-stone-500">Indicative pay</div>
                  <div className="text-2xl font-mono font-semibold text-emerald-800" data-testid="pay-total">
                    R{payrollSummary.pay.total.toFixed(2)}
                  </div>
                  <div className="text-[11px] text-stone-500 italic max-w-xs">
                    @ R{payrollSummary.user.hourly_rate.toFixed(2)}/h · {payrollSummary.pay.note}
                  </div>
                </div>
              </div>
              {payrollSummary.user.hourly_rate === 0 && (
                <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Your hourly rate is not set yet. Ask your admin to set it under Settings → Users to see the indicative pay figure.
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 p-3 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              data-testid="att-date-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              data-testid="att-date-to" />
          </div>
          {mode === 'manager' && (
            <>
              <div className="min-w-[180px]">
                <Label className="text-xs">Team member</Label>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger data-testid="att-user-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {teamUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[170px]">
                <Label className="text-xs">Sign-off status</Label>
                <Select value={signoffFilter} onValueChange={setSignoffFilter}>
                  <SelectTrigger data-testid="att-signoff-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="none">Neither signed</SelectItem>
                    <SelectItem value="employee_only">Employee only</SelectItem>
                    <SelectItem value="manager_only">Manager only</SelectItem>
                    <SelectItem value="both">Fully signed off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs">Source</Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger data-testid="att-source-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="biometric">Biometric (Live)</SelectItem>
                    <SelectItem value="timetec">TimeTec (History)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs">Day type</Label>
                <Select value={dayTypeFilter} onValueChange={setDayTypeFilter}>
                  <SelectTrigger data-testid="att-daytype-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="weekday">Weekdays only</SelectItem>
                    <SelectItem value="sunday">Sundays</SelectItem>
                    <SelectItem value="ph">Public holidays</SelectItem>
                    <SelectItem value="leave">Has leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-stone-400" />
                  <Input value={search} onChange={e => setSearch(e.target.value)}
                    className="pl-8" placeholder="Name, code, username…"
                    data-testid="att-search" />
                </div>
              </div>
            </>
          )}
          <Button variant="outline" size="sm" onClick={fetchRecords}>
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <button
            type="button"
            onClick={() => setPendingMineOnly(v => !v)}
            data-testid={`pending-mine-toggle-${mode}`}
            className={`inline-flex items-center gap-2 rounded-full text-xs font-medium px-3 py-1.5 border transition ${
              pendingMineOnly
                ? 'bg-amber-600 text-white border-amber-600 shadow-sm hover:bg-amber-700'
                : pendingMineCount > 0
                  ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                  : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
            }`}
            title={mode === 'staff'
              ? 'Records still needing your sign-off'
              : 'Records still needing your manager sign-off'}
          >
            <Hourglass className="w-3.5 h-3.5" />
            <span data-testid={`pending-mine-count-${mode}`}>{pendingMineCount}</span>
            <span>{mode === 'staff' ? 'awaiting my sign-off' : 'need my sign-off'}</span>
            {pendingMineOnly && <X className="w-3 h-3 ml-1" />}
          </button>
          <button
            type="button"
            onClick={() => setIncompleteOnly(v => !v)}
            data-testid={`incomplete-toggle-${mode}`}
            className={`inline-flex items-center gap-2 rounded-full text-xs font-medium px-3 py-1.5 border transition ${
              incompleteOnly
                ? 'bg-red-600 text-white border-red-600 shadow-sm hover:bg-red-700'
                : incompleteCount > 0
                  ? 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100'
                  : 'bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100'
            }`}
            title="Past records missing clock in or clock out (with other stamps present)"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span data-testid={`incomplete-count-${mode}`}>{incompleteCount}</span>
            <span>incomplete</span>
            {incompleteOnly && <X className="w-3 h-3 ml-1" />}
          </button>
          {mode === 'manager' && (
            <Button variant="ghost" size="sm" onClick={clearFilters}
              data-testid="att-clear-filters" className="text-stone-500">
              <Filter className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
        {(mode === 'manager' || pendingMineOnly || incompleteOnly) && (
          <div className="text-xs text-stone-500 flex items-center gap-1"
            data-testid="att-result-count">
            <span className="font-mono">{filteredRecords.length}</span>
            <span>of {records.length} record{records.length === 1 ? '' : 's'}</span>
            {records.length !== filteredRecords.length && (
              <span className="text-amber-700">· filtered</span>
            )}
            {pendingMineOnly && (
              <button
                type="button"
                onClick={() => setPendingMineOnly(false)}
                className="ml-2 underline text-amber-700 hover:text-amber-900"
                data-testid={`clear-pending-filter-${mode}`}
              >
                clear pending-only filter
              </button>
            )}
            {incompleteOnly && (
              <button
                type="button"
                onClick={() => setIncompleteOnly(false)}
                className="ml-2 underline text-red-700 hover:text-red-900"
                data-testid={`clear-incomplete-filter-${mode}`}
              >
                clear incomplete-only filter
              </button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-stone-700">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                {mode === 'manager' && <th className="text-left px-3 py-2">Staff</th>}
                <th className="text-left px-3 py-2">In</th>
                <th className="text-left px-3 py-2">Out</th>
                <th className="text-left px-3 py-2">Breaks</th>
                <th className="text-right px-3 py-2">Hours</th>
                <th className="text-right px-3 py-2">OT</th>
                <th className="text-left px-3 py-2">Leave</th>
                <th className="text-center px-3 py-2">Employee</th>
                <th className="text-center px-3 py-2">Manager</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={mode === 'manager' ? 11 : 10} className="text-center text-stone-400 py-6">Loading…</td></tr>
              )}
              {!loading && filteredRecords.length === 0 && (
                <tr><td colSpan={mode === 'manager' ? 11 : 10} className="text-center text-stone-400 py-6">No attendance records.</td></tr>
              )}
              {!loading && filteredRecords.map(r => {
                const myEmployeeSignoff = r.employee_signoff;
                const myManagerSignoff = r.manager_signoff;
                const showSignButton = (mode === 'staff' && !myEmployeeSignoff)
                                    || (mode === 'manager' && !myManagerSignoff);
                const incomplete = isIncompleteRecord(r);
                const incompleteReason = incomplete ? incompleteReasonFor(r) : null;
                return (
                  <tr
                    key={r.id}
                    className={`border-t border-stone-100 hover:bg-stone-50 ${
                      incomplete ? 'bg-red-50 hover:bg-red-100' : ''
                    }`}
                    data-testid={incomplete ? `att-row-incomplete-${r.id}` : undefined}
                  >
                    <td className="px-3 py-2 font-mono">
                      <div className="flex items-center gap-1.5">
                        {incomplete && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[10px] font-semibold px-1.5 py-0.5"
                            title={incompleteReason}
                          >
                            <AlertTriangle className="w-2.5 h-2.5" />
                            !
                          </span>
                        )}
                        <span>{r.date}</span>
                      </div>
                      <div className="text-xs">
                        {r.source === 'biometric'
                          ? <span className="text-emerald-700">Live</span>
                          : <span className="text-stone-400">History</span>}
                        {incomplete && (
                          <span className="ml-1 text-red-700 font-medium">
                            · {incompleteReason}
                          </span>
                        )}
                      </div>
                    </td>
                    {mode === 'manager' && (
                      <td className="px-3 py-2">
                        <div className="font-medium">{userMap[r.user_id]?.username || r.name}</div>
                        <div className="text-xs text-stone-500">{r.name}</div>
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono">{fmtTime(r.in_time)}</td>
                    <td className="px-3 py-2 font-mono">{fmtTime(r.out_time)}</td>
                    <td className="px-3 py-2 text-xs" data-testid={`att-breaks-${r.id}`}>
                      {(r.breaks && r.breaks.length > 0) ? (
                        <div className="space-y-0.5">
                          {r.breaks.map((b, i) => {
                            const mins = breakMinutes(b.out, b.in);
                            return (
                              <div key={i} className="flex items-center gap-1 font-mono">
                                <span className="text-stone-400 mr-0.5">{i + 1}.</span>
                                <span className="text-rose-600">{b.out || '?'}</span>
                                <span className="text-stone-400">→</span>
                                <span className="text-emerald-700">{b.in || '?'}</span>
                                {mins != null && (
                                  <span className="text-stone-500 ml-1">· {fmtBreakMins(mins)}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-stone-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(r.work_hours)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(r.ot_hours)}</td>
                    <td className="px-3 py-2 text-xs">{r.leave_type || '-'}</td>
                    <td className="px-3 py-2 text-center">
                      {myEmployeeSignoff
                        ? <span className="text-emerald-700 text-xs"
                                title={`${myEmployeeSignoff.by_username} · ${fmtDateLocal(myEmployeeSignoff.at)}`}>
                            <CheckCircle2 className="w-4 h-4 inline" />
                          </span>
                        : <Hourglass className="w-4 h-4 text-stone-300 inline" />}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {myManagerSignoff
                        ? <span className="text-emerald-700 text-xs"
                                title={`${myManagerSignoff.by_username} · ${fmtDateLocal(myManagerSignoff.at)}`}>
                            <ShieldCheck className="w-4 h-4 inline" />
                          </span>
                        : <Hourglass className="w-4 h-4 text-stone-300 inline" />}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {showSignButton && (
                        <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300"
                          onClick={() => signoff(r, 'sign')}
                          data-testid={`signoff-${mode}-${r.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Sign off
                        </Button>
                      )}
                      {/* Revoke intentionally hidden here — only the Master Admin
                          can revoke a sign-off, and they do so from the Admin
                          Attendance tab. Staff / Managers can request an edit
                          instead if something is wrong. */}
                      <Button size="sm" variant="ghost"
                        onClick={() => { setOpenEdit(r); setEditReason(''); setEditChanges(''); }}
                        data-testid={`request-edit-${r.id}`}>
                        <Edit3 className="w-3 h-3 mr-1" /> Request edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-2 text-xs text-stone-500 border-t border-stone-200">
          {filteredRecords.length} record(s)
        </div>
      </div>

      <Dialog open={!!openEdit} onOpenChange={o => { if (!o) setOpenEdit(null); }}>
        <DialogContent data-testid="edit-request-dialog">
          {openEdit && (
            <>
              <DialogHeader>
                <DialogTitle>Request attendance edit</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="bg-stone-50 rounded p-3 text-xs space-y-1">
                  <div><b>Staff:</b> {openEdit.name || userMap[openEdit.user_id]?.username || 'You'}</div>
                  <div><b>Date:</b> {openEdit.date}</div>
                  <div><b>Recorded:</b> In {fmtTime(openEdit.in_time)} · Out {fmtTime(openEdit.out_time)} · {fmtNum(openEdit.work_hours)}h</div>
                  {openEdit.breaks && openEdit.breaks.length > 0 && (
                    <div>
                      <b>Breaks:</b>{' '}
                      {openEdit.breaks.map((b, i) => {
                        const mins = breakMinutes(b.out, b.in);
                        return (
                          <span key={i} className="mr-2 font-mono">
                            {b.out || '?'} → {b.in || '?'}
                            {mins != null && <span className="text-stone-500"> ({mins}m)</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Reason</Label>
                  <Textarea rows={2} value={editReason} onChange={e => setEditReason(e.target.value)}
                    placeholder="Why does this need to change?" data-testid="edit-reason-input" />
                </div>
                <div>
                  <Label className="text-xs">Requested changes</Label>
                  <Textarea rows={3} value={editChanges} onChange={e => setEditChanges(e.target.value)}
                    placeholder="e.g. In should be 06:42 not 06:53; lunch missing"
                    data-testid="edit-changes-input" />
                </div>
                <div className="text-xs text-stone-500">
                  Master Admin will review, make the correction in TimeTec, and approve here to pull the updated record.
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={() => setOpenEdit(null)}>Cancel</Button>
                  <Button disabled={submitting} onClick={submitEdit}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="submit-edit-request-button">
                    {submitting ? 'Submitting…' : 'Submit request'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AttendanceView;
